/**
 * Per-entity visuals.
 *
 * Everything here is three.js primitives and hand-built BufferGeometry; the
 * repo ships no models. The rule the shapes follow is that a silhouette read
 * at the edge of the fog frightens more than anatomy read up close, so the
 * budget goes on proportion and motion rather than surface detail. Nothing has
 * a face unless the face is the point.
 *
 * Geometry and materials are cached per kind and reference counted, so three
 * hounds cost one set of buffers. Materials whose opacity is animated per
 * instance (smiler, hollow) are cloned from the shared originals — otherwise
 * two of the same kind would fight over one uniform.
 *
 * The six kinds below are the ones invented for this game. The canonical
 * Backrooms entities are a file each, `ent*.ts`, and share the toolbox in
 * `entkit.ts`; this file only routes to them.
 */

import * as THREE from 'three';
import { angleDelta, clamp, clamp01, damp, lerp, noise1, renderYaw, smoothstep, TAU } from '../core/math';
import { makeRNG } from '../core/rng';
import type { EntityKind } from '../core/types';
import type { EntityView } from './api';
import { createBacteriaView } from './entbacteria';
import { createCentipedeView } from './entcentipede';
import { createDeathmothView } from './entdeathmoth';
import { createFacelingView } from './entfaceling';
import {
  bakeAlpha,
  bone,
  chain,
  dampAngle,
  freeAll,
  Kit,
  mergeParts,
  planarSpeed,
  taperZ,
  type Disposable,
} from './entkit';
import { createPartygoerView } from './entpartygoer';

/** Scratch vectors — the update path must not allocate. */
const _dir = new THREE.Vector3();
const _fwd = new THREE.Vector3();

/** A soft radial falloff, used for the smiler's eye and mouth bloom. */
function makeGlowTexture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.3, 'rgba(255,255,255,0.38)');
  g.addColorStop(0.65, 'rgba(255,255,255,0.09)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/* ------------------------------------------------------------------ *
 * Hound — low, fast, too many joints, mostly jaw
 * ------------------------------------------------------------------ */

interface HoundRig {
  root: THREE.Group;
  body: THREE.Group;
  spine: THREE.Mesh;
  neck: THREE.Group;
  head: THREE.Group;
  jaw: THREE.Group;
  legs: THREE.Group[][];
}

interface HoundKit extends Disposable {
  make(): HoundRig;
}

const HOUND_HIP_Y = 0.66;
const HOUND_NECK_TILT = 0.85;

function buildHoundKit(): HoundKit {
  // Near-black and matte: under a flashlight it lifts just enough to prove it
  // is solid, and never enough to show what it is.
  const hide = new THREE.MeshLambertMaterial({ color: 0x0a0a0c });
  const skull = new THREE.MeshLambertMaterial({ color: 0x050506 });

  const spineGeo = new THREE.CapsuleGeometry(0.185, 0.62, 3, 6);
  spineGeo.rotateX(Math.PI / 2);
  const chestGeo = new THREE.SphereGeometry(0.215, 6, 4);
  chestGeo.translate(0, 0.0, -0.3);
  const haunchGeo = new THREE.SphereGeometry(0.2, 6, 4);
  haunchGeo.translate(0, 0.02, 0.36);
  const tailGeo = bone(0.42, 0.045, 0.012, 4);
  const neckGeo = bone(0.24, 0.1, 0.085, 5);

  const braincaseGeo = new THREE.SphereGeometry(0.078, 5, 4);
  braincaseGeo.scale(1.2, 1.0, 1.35);
  // Translate before tapering: the taper bounds are in head-local space, and
  // the snout has to be a third of the animal's length to read as jaw.
  const snoutGeo = new THREE.BoxGeometry(0.115, 0.085, 0.3);
  snoutGeo.translate(0, 0.005, -0.2);
  taperZ(snoutGeo, -0.05, -0.35, 0.5);
  const jawGeo = new THREE.BoxGeometry(0.1, 0.05, 0.28);
  jawGeo.translate(0, -0.02, -0.145);
  taperZ(jawGeo, 0, -0.29, 0.55);

  // Four bones per leg instead of the two a dog has. The extra joint is the
  // whole point: at speed the gait folds in a way no animal's does.
  const frontLens = [0.25, 0.22, 0.13, 0.08];
  const backLens = [0.26, 0.25, 0.11, 0.06];
  const frontRadii = [0.055, 0.046, 0.036, 0.03, 0.024];
  const backRadii = [0.062, 0.05, 0.038, 0.03, 0.024];
  const frontGeos = frontLens.map((l, i) => bone(l, frontRadii[i]!, frontRadii[i + 1]!, 4));
  const backGeos = backLens.map((l, i) => bone(l, backRadii[i]!, backRadii[i + 1]!, 4));

  const geos = [
    spineGeo,
    chestGeo,
    haunchGeo,
    tailGeo,
    neckGeo,
    braincaseGeo,
    snoutGeo,
    jawGeo,
    ...frontGeos,
    ...backGeos,
  ];

  return {
    make(): HoundRig {
      const root = new THREE.Group();
      const body = new THREE.Group();
      body.position.y = HOUND_HIP_Y;
      root.add(body);

      const spine = new THREE.Mesh(spineGeo, hide);
      spine.position.z = 0.05;
      body.add(spine, new THREE.Mesh(chestGeo, hide), new THREE.Mesh(haunchGeo, hide));

      const tail = new THREE.Group();
      tail.position.set(0, 0.06, 0.5);
      tail.rotation.x = -0.9;
      tail.add(new THREE.Mesh(tailGeo, hide));
      body.add(tail);

      const neck = new THREE.Group();
      neck.position.set(0, 0.02, -0.42);
      neck.rotation.x = HOUND_NECK_TILT;
      neck.add(new THREE.Mesh(neckGeo, hide));
      body.add(neck);

      const head = new THREE.Group();
      head.position.y = -0.24;
      head.rotation.x = -HOUND_NECK_TILT - 0.3;
      head.add(new THREE.Mesh(braincaseGeo, skull), new THREE.Mesh(snoutGeo, skull));
      neck.add(head);

      const jaw = new THREE.Group();
      jaw.position.set(0, -0.032, -0.045);
      jaw.add(new THREE.Mesh(jawGeo, skull));
      head.add(jaw);

      const legs: THREE.Group[][] = [];
      const roots: Array<[number, number, THREE.BufferGeometry[], number[]]> = [
        [-0.155, -0.3, frontGeos, frontLens],
        [0.155, -0.3, frontGeos, frontLens],
        [-0.165, 0.36, backGeos, backLens],
        [0.165, 0.36, backGeos, backLens],
      ];
      for (const [x, z, g, lens] of roots) {
        const joints = chain(g, lens, hide);
        joints[0]!.position.set(x, -0.02, z);
        body.add(joints[0]!);
        legs.push(joints);
      }

      root.traverse((o) => {
        o.castShadow = true;
      });
      return { root, body, spine, neck, head, jaw, legs };
    },
    dispose(): void {
      freeAll(geos);
      freeAll([hide, skull]);
    },
  };
}

const houndKit = new Kit(buildHoundKit);

/** Trot pairing: diagonal legs move together until the lunge breaks it. */
const HOUND_GAIT_PHASE = [0, Math.PI, Math.PI, 0];

function createHoundView(): EntityView {
  const kit = houndKit.acquire();
  const rig = kit.make();
  const object = new THREE.Group();
  object.add(rig.root);

  // Per-instance so two hounds never step in lockstep. Cosmetic only.
  const phase = Math.random() * TAU;
  let gait = phase;
  let lunge = 0;
  let dead = 0;
  let yaw = 0;
  let yawInit = false;

  return {
    object,
    update(state, dt, time, _camera) {
      if (!yawInit) {
        yaw = state.yaw;
        yawInit = true;
      }
      yaw = dampAngle(yaw, state.yaw, 9, dt);
      object.position.set(state.pos.x, 0, state.pos.z);
      object.rotation.y = renderYaw(yaw);

      const speed = planarSpeed(state);
      const drive = clamp01(speed / 4.2);
      gait += dt * (2.0 + speed * 2.8);
      lunge = damp(lunge, state.mode === 'attack' ? 1 : 0, 9, dt);
      dead = damp(dead, state.mode === 'dead' ? 1 : 0, 3.2, dt);

      const swing = 0.16 + drive * 0.78;
      for (let i = 0; i < 4; i++) {
        const joints = rig.legs[i]!;
        const p = gait + HOUND_GAIT_PHASE[i]!;
        // The lunge collapses the trot into a bound: the front pair reaches
        // together while the back pair kicks.
        const reach = Math.sin(p) * (1 - lunge) + Math.sin(gait + (i < 2 ? 0 : Math.PI)) * lunge;
        joints[0]!.rotation.x = reach * swing * 0.72 + (i < 2 ? 0.12 : -0.1);
        joints[1]!.rotation.x = -Math.max(0, Math.sin(p + 0.85)) * swing * 1.15 - 0.18;
        joints[2]!.rotation.x = Math.sin(p + 1.7) * swing * 0.55 + 0.24;
        joints[3]!.rotation.x = -Math.sin(p + 2.4) * swing * 0.4;
      }

      const bob = Math.sin(gait * 2) * 0.022 * drive;
      rig.root.position.y = bob - lunge * 0.07 - dead * 0.2;
      rig.root.position.z = -lunge * 0.3;
      rig.root.rotation.z = Math.sin(gait) * 0.05 * drive + dead * 1.35;
      rig.body.rotation.x = -0.05 * drive + lunge * 0.12;
      rig.spine.scale.z = 1 + lunge * 0.22;

      // Idle is a slow, blind cast of the head — it hunts by sound, so the
      // head is always searching even when the body is still.
      rig.neck.rotation.x = HOUND_NECK_TILT - lunge * 0.5 + Math.sin(time * 0.7 + phase) * 0.06;
      rig.head.rotation.y = Math.sin(time * 0.53 + phase) * 0.16 * (1 - lunge);
      rig.head.rotation.z = Math.sin(time * 0.31 + phase) * 0.1 * (1 - lunge);
      rig.jaw.rotation.x = -(0.1 + Math.sin(time * 0.95 + phase) * 0.05 + lunge * 0.78);
    },
    dispose() {
      object.clear();
      houndKit.release();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Smiler — two eyes and a grin, hung in the dark
 * ------------------------------------------------------------------ */

const SMILER_EYE_Y = 1.868;
const SMILER_GRIN_Y = 1.781;

/**
 * Half-width of the figure at each height, from the floor to the crown: a
 * heavy body, shoulders, a jawline pinch and a head. Splined rather than
 * listed vertex by vertex so the outline stays smooth at any row count.
 */
const SMILER_PROFILE: ReadonlyArray<readonly [number, number]> = [
  [0.0, 0.3],
  [0.35, 0.285],
  [0.72, 0.268],
  [1.12, 0.283],
  [1.42, 0.296],
  [1.58, 0.268],
  [1.68, 0.176],
  [1.735, 0.118],
  [1.8, 0.13],
  [1.88, 0.138],
  [1.955, 0.122],
  [2.005, 0.082],
  [2.035, 0.04],
  [2.05, 0.005],
];

/**
 * A standing figure reduced to its outline, built as a vertical strip rather
 * than a filled polygon. The strip matters: it guarantees every triangle spans
 * one short band of height, so the alpha ramp that dissolves the legs runs
 * evenly up the body instead of streaking across whatever a general-purpose
 * triangulator produced.
 */
function makeSmilerSilhouette(): THREE.BufferGeometry {
  const spline = new THREE.SplineCurve(SMILER_PROFILE.map(([y, w]) => new THREE.Vector2(y, w)));
  const rows = spline.getPoints(36);

  const position = new Float32Array(rows.length * 2 * 3);
  const normal = new Float32Array(rows.length * 2 * 3);
  const rgba = new Float32Array(rows.length * 2 * 4);
  const index: number[] = [];

  for (let i = 0; i < rows.length; i++) {
    const y = rows[i]!.x;
    const w = Math.max(rows[i]!.y, 0.004);
    // The two sides are not mirrors. Nothing about it is quite even.
    const left = -w * (1 + 0.05 * Math.sin(y * 2.3 + 0.6));
    // It does not stand so much as end. The legs are simply absent.
    const alpha = smoothstep(0.02, 0.66, y);
    for (let s = 0; s < 2; s++) {
      const v = i * 2 + s;
      position[v * 3] = s === 0 ? left : w;
      position[v * 3 + 1] = y;
      normal[v * 3 + 2] = 1;
      rgba[v * 4] = 1;
      rgba[v * 4 + 1] = 1;
      rgba[v * 4 + 2] = 1;
      rgba[v * 4 + 3] = alpha;
    }
    if (i > 0) {
      const a = (i - 1) * 2;
      index.push(a, a + 1, a + 3, a, a + 3, a + 2);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(position, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(normal, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(rgba, 4));
  geo.setIndex(index);
  return geo;
}

/** An almond that leans: the top arc is fuller than the bottom, so it squints. */
function makeEyeGeometry(): THREE.BufferGeometry {
  const s = new THREE.Shape();
  const w = 0.043;
  s.moveTo(-w, 0);
  s.quadraticCurveTo(0.002, 0.021, w, 0.001);
  s.quadraticCurveTo(0.004, -0.013, -w, 0);
  return new THREE.ShapeGeometry(s, 8);
}

/**
 * The grin: two arcs with slightly different sag, so the shape tapers to
 * nothing at both ends and is barely a centimetre thick in the middle. Wide
 * enough to reach the edge of the face. No teeth — teeth turn it into a
 * cartoon.
 */
function makeGrinGeometry(): THREE.BufferGeometry {
  const s = new THREE.Shape();
  s.moveTo(-0.1, 0.012);
  s.quadraticCurveTo(0.006, -0.108, 0.104, -0.004);
  s.quadraticCurveTo(0.008, -0.079, -0.1, 0.012);
  return new THREE.ShapeGeometry(s, 12);
}

interface SmilerKit extends Disposable {
  silhouette: THREE.BufferGeometry;
  eye: THREE.BufferGeometry;
  grin: THREE.BufferGeometry;
  glow: THREE.BufferGeometry;
  bodyMat: THREE.MeshBasicMaterial;
  featureMat: THREE.MeshBasicMaterial;
  glowMat: THREE.MeshBasicMaterial;
}

function buildSmilerKit(): SmilerKit {
  const silhouette = makeSmilerSilhouette();
  const eye = makeEyeGeometry();
  const grin = makeGrinGeometry();
  const glow = new THREE.PlaneGeometry(1, 1);
  const glowTex = makeGlowTexture();

  // Fog on the body so it dissolves into the murk at range; fog off the
  // features so the eyes and the grin are the last things left out there.
  const bodyMat = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.95,
    vertexColors: true,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const featureMat = new THREE.MeshBasicMaterial({
    color: 0xffeec4,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide,
    depthWrite: false,
    fog: false,
  });
  const glowMat = new THREE.MeshBasicMaterial({
    map: glowTex,
    color: 0xffe3a0,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    fog: false,
  });

  return {
    silhouette,
    eye,
    grin,
    glow,
    bodyMat,
    featureMat,
    glowMat,
    dispose(): void {
      freeAll([silhouette, eye, grin, glow, bodyMat, featureMat, glowMat, glowTex]);
    },
  };
}

const smilerKit = new Kit(buildSmilerKit);

function createSmilerView(): EntityView {
  const kit = smilerKit.acquire();
  // Cloned so each smiler owns its own fade; the textures stay shared.
  const bodyMat = kit.bodyMat.clone();
  const featureMat = kit.featureMat.clone();
  const glowMat = kit.glowMat.clone();

  const object = new THREE.Group();
  const rig = new THREE.Group();
  object.add(rig);

  const body = new THREE.Mesh(kit.silhouette, bodyMat);
  rig.add(body);

  const mouthGlow = new THREE.Mesh(kit.glow, glowMat);
  mouthGlow.scale.set(0.34, 0.15, 1);
  mouthGlow.position.set(0.002, SMILER_GRIN_Y - 0.032, 0.004);
  mouthGlow.renderOrder = 1;
  rig.add(mouthGlow);

  for (const side of [-1, 1] as const) {
    const halo = new THREE.Mesh(kit.glow, glowMat);
    halo.scale.set(0.15, 0.15, 1);
    halo.position.set(side * 0.056, SMILER_EYE_Y, 0.005);
    halo.renderOrder = 1;
    rig.add(halo);

    const iris = new THREE.Mesh(kit.eye, featureMat);
    iris.position.set(side * 0.056, SMILER_EYE_Y, 0.009);
    iris.rotation.z = side * -0.17;
    iris.renderOrder = 2;
    rig.add(iris);
  }

  const grin = new THREE.Mesh(kit.grin, featureMat);
  grin.position.set(0.004, SMILER_GRIN_Y, 0.009);
  grin.rotation.z = -0.025;
  grin.renderOrder = 2;
  rig.add(grin);

  const phase = Math.random() * TAU;
  let stare = 0;
  let creepX = 0;
  let creepZ = 0;
  let dead = 0;

  return {
    object,
    update(state, dt, time, camera) {
      const toCamX = camera.position.x - state.pos.x;
      const toCamZ = camera.position.z - state.pos.z;
      const flat = Math.hypot(toCamX, toCamZ) || 1e-4;
      const nx = toCamX / flat;
      const nz = toCamZ / flat;

      object.position.set(state.pos.x + creepX, 0, state.pos.z + creepZ);
      // Billboard about Y only. A sheet that also tips reads as paper.
      object.rotation.y = Math.atan2(nx, nz);

      // Using the camera's local transform rather than its world matrix keeps
      // this in step with the frame: the renderer places the camera at the
      // scene root and matrixWorld has not been refreshed yet.
      _dir.set(state.pos.x - camera.position.x, SMILER_EYE_Y - camera.position.y, state.pos.z - camera.position.z);
      const dist = _dir.length() || 1e-4;
      _dir.multiplyScalar(1 / dist);
      _fwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
      // ~9.5 degrees off centre. Glancing past it does not count as looking.
      const staring = _dir.dot(_fwd) > 0.986 && dist < 26;

      stare = damp(stare, staring ? 1 : 0, staring ? 0.35 : 0.9, dt);
      // It does not move while you watch it. It has moved when you look back.
      // The offset is world-space and never repays itself.
      const step = stare * stare * dt * 0.1;
      if (creepX * creepX + creepZ * creepZ < 0.9 * 0.9) {
        creepX += nx * step;
        creepZ += nz * step;
      }

      dead = damp(dead, state.mode === 'dead' ? 1 : 0, 2, dt);
      const near = 1 - smoothstep(14, 36, dist);
      const alive = 1 - dead;
      bodyMat.opacity = (0.18 + 0.78 * near) * (1 - 0.3 * stare) * alive;
      featureMat.opacity = (0.3 + 0.66 * near) * (1 - 0.72 * stare) * alive;
      glowMat.opacity = (0.14 + 0.4 * near) * (1 - 0.8 * stare) * alive;

      // Almost motionless. The stillness is the horror; anything more than a
      // breath of drift makes it a monster instead of a fact.
      rig.position.y = Math.sin(time * 0.29 + phase) * 0.011 - dead * 0.4;
      rig.rotation.z = Math.sin(time * 0.17 + phase) * 0.013;
    },
    dispose() {
      object.clear();
      freeAll([bodyMat, featureMat, glowMat]);
      smilerKit.release();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Clump — a growth that breathes
 * ------------------------------------------------------------------ */

interface LumpSpec {
  x: number;
  y: number;
  z: number;
  sx: number;
  sy: number;
  sz: number;
  phase: number;
  bulb: boolean;
}

interface ClumpKit extends Disposable {
  lump: THREE.BufferGeometry;
  flesh: THREE.Material;
  bulb: THREE.Material;
  spec: readonly LumpSpec[];
}

function buildClumpKit(): ClumpKit {
  const lump = new THREE.SphereGeometry(1, 6, 4);
  const flesh = new THREE.MeshLambertMaterial({ color: 0x2e1c1c, emissive: 0x150406 });
  // Not light so much as the suggestion that something under the surface is
  // lit. Kept below anything the flashlight adds so it never looks like paint.
  const bulb = new THREE.MeshLambertMaterial({ color: 0x40202a, emissive: 0x3a0c1c });

  const rng = makeRNG(0x51a7c0);
  const spec: LumpSpec[] = [];
  const count = 11;
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    const ang = rng.range(0, TAU);
    const rad = rng.range(0, 0.42) * (1 - t * 0.55);
    const r = lerp(0.3, 0.13, t) * rng.range(0.8, 1.15);
    spec.push({
      x: Math.cos(ang) * rad,
      y: lerp(0.14, 0.5, t * t) + rng.range(-0.03, 0.06),
      z: Math.sin(ang) * rad,
      sx: r * rng.range(0.85, 1.25),
      sy: r * rng.range(0.7, 1.1),
      sz: r * rng.range(0.85, 1.25),
      phase: rng.range(0, TAU),
      bulb: rng.chance(0.3),
    });
  }

  return {
    lump,
    flesh,
    bulb,
    spec,
    dispose(): void {
      freeAll([lump, flesh, bulb]);
    },
  };
}

const clumpKit = new Kit(buildClumpKit);

function createClumpView(): EntityView {
  const kit = clumpKit.acquire();
  const object = new THREE.Group();
  const rig = new THREE.Group();
  object.add(rig);

  const meshes: THREE.Mesh[] = [];
  for (const s of kit.spec) {
    const mesh = new THREE.Mesh(kit.lump, s.bulb ? kit.bulb : kit.flesh);
    mesh.position.set(s.x, s.y, s.z);
    mesh.castShadow = true;
    rig.add(mesh);
    meshes.push(mesh);
  }

  const phase = Math.random() * TAU;

  return {
    object,
    update(state, _dt, time, _camera) {
      object.position.set(state.pos.x, 0, state.pos.z);
      object.rotation.y = renderYaw(state.yaw);

      // Each lump breathes on its own clock, so the mound never pulses as one
      // object. Slow enough that you have to stand still to be sure.
      for (let i = 0; i < meshes.length; i++) {
        const s = kit.spec[i]!;
        const b = 1 + Math.sin(time * 0.55 + s.phase) * 0.05;
        meshes[i]!.scale.set(s.sx * b, s.sy * b, s.sz * b);
      }
      rig.rotation.z = Math.sin(time * 0.21 + phase) * 0.02;
    },
    dispose() {
      object.clear();
      clumpKit.release();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Hollow — a shape the player's own head put there
 * ------------------------------------------------------------------ */

interface HollowKit extends Disposable {
  figure: THREE.BufferGeometry;
  material: THREE.MeshBasicMaterial;
}

function buildHollowKit(): HollowKit {
  // It never articulates, so the whole figure is one buffer carrying one
  // continuous alpha ramp: hands, feet and crown fade out of existence.
  const parts: THREE.BufferGeometry[] = [];

  const torso = new THREE.CapsuleGeometry(0.165, 0.42, 3, 7);
  torso.translate(0, 1.16, 0);
  parts.push(torso);

  const head = new THREE.SphereGeometry(0.13, 7, 5);
  head.translate(0, 1.62, 0);
  parts.push(head);

  for (const side of [-1, 1] as const) {
    const arm = new THREE.CapsuleGeometry(0.062, 0.46, 3, 5);
    arm.rotateZ(side * 0.16);
    arm.translate(side * 0.23, 1.12, 0);
    parts.push(arm);

    const leg = new THREE.CapsuleGeometry(0.082, 0.58, 3, 5);
    leg.translate(side * 0.105, 0.42, 0);
    parts.push(leg);
  }

  const figure = bakeAlpha(mergeParts(parts), (x, y, z) => {
    const crown = 1 - smoothstep(1.5, 1.78, y);
    const floor = smoothstep(0.02, 0.5, y);
    const edge = 1 - 0.6 * smoothstep(0.14, 0.3, Math.hypot(x, z));
    return crown * floor * edge;
  });

  // Basic, not Lambert: pointing a flashlight at it changes nothing, which is
  // the tell. Never writes depth, never casts.
  const material = new THREE.MeshBasicMaterial({
    color: 0x848a92,
    transparent: true,
    opacity: 0.22,
    vertexColors: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  return {
    figure,
    material,
    dispose(): void {
      freeAll([figure, material]);
    },
  };
}

const hollowKit = new Kit(buildHollowKit);

function createHollowView(): EntityView {
  const kit = hollowKit.acquire();
  const material = kit.material.clone();

  const object = new THREE.Group();
  const rig = new THREE.Group();
  const mesh = new THREE.Mesh(kit.figure, material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  rig.add(mesh);
  object.add(rig);

  // Fixed per instance so two hollows never flicker in unison. Cosmetic.
  const seed = (Math.random() * 0xffff) | 0;
  const drift = Math.random() * 100;

  return {
    object,
    update(state, _dt, time, camera) {
      object.position.set(state.pos.x, 0, state.pos.z);
      object.rotation.y = renderYaw(state.yaw);

      const dist = Math.hypot(camera.position.x - state.pos.x, camera.position.z - state.pos.z);
      const near = 1 - smoothstep(10, 30, dist);

      // Two rates: a fast shimmer, and a slow gate that drops it out entirely
      // for a beat or two. Noise rather than random, so it holds rather than
      // buzzing at the frame rate.
      const shimmer = 0.42 + 0.58 * noise1(time * 2.6, seed);
      const present = noise1(time * 0.55, seed + 977) > 0.8 ? 0.05 : 1;
      material.opacity = (0.1 + 0.24 * near) * shimmer * present;

      // It does not walk. It is simply nearer, or it is not there.
      rig.position.x = (noise1(time * 0.6 + drift, seed + 31) - 0.5) * 0.05;
      rig.position.z = (noise1(time * 0.6 + drift, seed + 63) - 0.5) * 0.05;
      rig.rotation.y = (noise1(time * 0.31 + drift, seed + 95) - 0.5) * 0.4;
    },
    dispose() {
      object.clear();
      material.dispose();
      hollowKit.release();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Entry point
 * ------------------------------------------------------------------ */

/**
 * Build the visual for one entity. The view owns its own placement: it reads
 * `state.pos` and `state.yaw` every frame and is authored with the floor at
 * y = 0, so the renderer only has to parent it and call `update`.
 */
export function createEntityView(kind: EntityKind): EntityView {
  switch (kind) {
    case 'hound':
      return createHoundView();
    case 'smiler':
      return createSmilerView();
    case 'clump':
      return createClumpView();
    case 'hollow':
      return createHollowView();

    // The canonical roster. One file each; see the header of any of them for
    // which parts are documented and which are ours.
    case 'bacteria':
      return createBacteriaView();
    case 'faceling':
      return createFacelingView();
    case 'deathmoth':
      return createDeathmothView();
    case 'partygoer':
      return createPartygoerView();
    case 'centipede':
      return createCentipedeView();
  }
}
